import { DatePipe, getCurrencySymbol } from '@angular/common';
import { Injectable } from '@angular/core';
import { UserService } from '@app/app-state/user.service';
import { updatedTypeMeta } from '@app/dashboard-measurement/types';
import { ChartDataInterface, Series } from '@app/models/chart-data.model';
import { User } from '@app/models/user.model';
import { measurement } from '@lib/measurement';
import { Unit } from '@lib/measurement/units';
import { BehaviorSubject } from 'rxjs';
import { SiteService } from '../app-state/site.service';
import { DrillAndBlastCostsStructure, DrillAndBlastDataStructure, FielderAnalysisDataStructure, FragmentationStructure , InventoryStructure } from './data-structures';
import * as Highcharts from 'highcharts';
import { math } from "@lib/math";
import { StockpileData } from '@app/modules/inventory-dashboard/inventory-dashboard.component';
import { Dataset } from '@app/models/dataset.model';
import { Site } from '@app/models/site.model';

interface ChartConfig {
	dataField: string;
	dataFieldTwo?: string;
	chartType: string;
	chartTitle: string;
	seriesType?: string;
	seriesType1?: string;
	valueUnit: keyof typeof updatedTypeMeta;
	yAxisLabel: string;
	xAxisLabel?: string;
	isAverage?: boolean;
	isCombineType?: boolean;
	isDesignBurdenGraph?: boolean;
	isDonut?: boolean;
	isDrillAndBlastCosts?: boolean;
	isDrillName?: boolean;
	isExplosiveGraph?: boolean;
	isFinesPercent?: boolean;
	isFragmentation?: boolean;
	isHoleFeature?: boolean;
	isInventoryGraph?: boolean;
	isLabourGraph?: boolean;
	isOversizePercent?: boolean;
	isSizeRange?: boolean;
	isTruckGraph?: boolean;
}

interface ChartResponseRecord {
	[key: string]: unknown;
	domainId?: number;
	subdomainId?: number;
	siteId?: number;
	datasetCreatedAt?: string;
	createdAt?: string;
	datasetName?: string;
	drillMachineName?: string;
	fragmentationSizeRanges?: { rocksAreaPercent?: number[] };
	finesPercent?: number;
	holeFeatures?: Record<string, number>;
	noOfLabors?: number;
	hours?: number;
	trucks?: Array<{ code: string; [key: string]: unknown }>;
	minimumRocksDiameter?: number;
	oversizeGoal?: number;
	datasetId?: number;
	finesGoal?: number;
	designBurden?: number;
	currencyCode?: string;
}

@Injectable({
	providedIn: 'root'
})


export class GenerateChartDataService {

	public drillAndBlastDataStructure = DrillAndBlastDataStructure;
	public fielderAnalysisDataStructure = FielderAnalysisDataStructure;
	public fragmentationStructure = FragmentationStructure;
	public drillAndBlastCostsStructure = DrillAndBlastCostsStructure;
	public inventoryStructure = InventoryStructure;

	public originalDrillAndBalstData: ChartDataInterface[] =[];
	public originalInventoryData: ChartDataInterface[] =[];
	public originalFielderAnalytics:ChartDataInterface[] = [];
	public originalFragmentationData:ChartDataInterface[] = [];
	public originalFielderAnalyticsByDrill:ChartDataInterface[]= [];
	public filteredData: ChartDataInterface[] = [];

	private siteId :number;
	private userUnitMeasurement:string;

	private chartOptionsSubject = new BehaviorSubject<Highcharts.Options[] | null>(null);
	chartOptions$ = this.chartOptionsSubject.asObservable();

	constructor(
		private siteService:SiteService,
		private datePipe: DatePipe,
		private userService: UserService,
		private user: User) {
		this.siteService.siteId$.subscribe(siteId => this.siteId = siteId);
		this.userService.userUnitMeasurement$.subscribe(
			measurementUnit => this.userUnitMeasurement = measurementUnit
		)
	}

	// TODO
	public getFilteredData<T>(originalData: T[], domainId: number, subdomainId: number, siteId: number, startDate: string, endDate: string, selectedTimeline: string): T[] {
		return originalData?.filter((item: T) => {
			const data = item as T & { domainId?: number; subdomainId?: number; siteId?: number; datasetCreatedAt?: string; createdAt?: string };
			let matches = true;
			if (domainId) {
				matches = matches && data.domainId === domainId;
			}

			if (subdomainId) {
				matches = matches && data.subdomainId === subdomainId;
			}

			if (siteId) {
				matches = matches && data.siteId === siteId;
			}

			if (startDate) {
				let formattedDate = this.datePipe.transform(data.datasetCreatedAt ? data.datasetCreatedAt : data.createdAt, 'yyyy-MM-dd', 'UTC');
				matches = matches && formattedDate >= startDate;
			}

			if (endDate) {
				let formattedDate = this.datePipe.transform(data.datasetCreatedAt ? data.datasetCreatedAt : data.createdAt, 'yyyy-MM-dd', 'UTC');
				matches = matches && formattedDate >= startDate && formattedDate <= endDate;
			}

			if(selectedTimeline){
				const timelineDate = this.processTimelineDate(selectedTimeline);
				let formattedDate = this.datePipe.transform(data.datasetCreatedAt ? data.datasetCreatedAt : data.createdAt, 'yyyy-MM-dd', 'UTC');
				matches = matches && formattedDate >= timelineDate;
			}
			return matches;
		});
	}


	private getDataStructure() {
		switch (this.siteService.activeSideNav) {
			case 'drillAndBlast':
				return this.drillAndBlastDataStructure;

			case 'fielder':
				return this.fielderAnalysisDataStructure;

			case 'fragmentation':
				return this.fragmentationStructure;

			case 'inventory':
				return this.inventoryStructure;

			case 'drillAndBlastCosts':
				return this.drillAndBlastCostsStructure;

			default:
				return null;
		}
	}

	// TODO
	public generateChartData(response: unknown, isPfAndTon?: boolean) {
		const dataStructure = (isPfAndTon ? this.drillAndBlastDataStructure : this.getDataStructure()) as unknown as Record<string, Record<string, ChartConfig[]>>;
		const chartOptionsObject: Record<string, Record<string, object[]>> = {};

		if (!dataStructure) {
			return chartOptionsObject;
		}
		const typedResponse = response as ChartResponseRecord[];

		Object.keys(dataStructure).forEach(sectionKey => {
			chartOptionsObject[sectionKey] = {};

			const sectionData = dataStructure[sectionKey];

			Object.keys(sectionData).forEach((graphKey: string) => {
				chartOptionsObject[sectionKey][graphKey] = [];

				const graphConfigurations = sectionData[graphKey];
				// TODO
				graphConfigurations.forEach((config: ChartConfig) => {
					const metricUnit = updatedTypeMeta[config.valueUnit].metricUnit as Unit;
					const imperialUnit = updatedTypeMeta[config.valueUnit].imperialUnit as Unit;

					if (response) {
						const chartOptions = this.getChartOptions(config, typedResponse, imperialUnit, metricUnit, this.userService.user.unit(config.valueUnit), );
						chartOptionsObject[sectionKey][graphKey].push(chartOptions);

						const graphKeyConditions = {
							'holeCountGraph': 4,
						};

						if (graphKeyConditions.hasOwnProperty(graphKey) && chartOptionsObject[sectionKey][graphKey].length === graphKeyConditions[graphKey] && !this.siteId) {
							chartOptionsObject[sectionKey][graphKey].splice(-1, 1);
						}

						if (this.siteService.activeSideNav === 'drillAndBlast' && graphKey === 'burdenSpacinGraph') {
							const burdenGraphOption = [];
							burdenGraphOption.push(chartOptions);
							this.chartOptionsSubject.next(burdenGraphOption);
						}

					}
				});
			});

		});

		return chartOptionsObject;
	}

	// TODO
	private getChartOptions(config: ChartConfig, response: ChartResponseRecord[], imperialUnit: Unit, metricUnit: Unit, unit: Unit) {
		let datasetName:string[];

		const unitAbbreviation = measurement.abbreviation(unit);
		let toUnit:Unit = this.userUnitMeasurement === 'imperial' ? imperialUnit : metricUnit;
		const processCost = (cost: ChartResponseRecord, label: string, toUnit: Unit) => {
			const convertedValue = measurement.convert(Number(cost[label]), toUnit)
			return Math.round(convertedValue * 100) / 100;
		};
		const label1 = config.dataField;
		const label2 = config.dataFieldTwo;
		let yAxisLabel = config.yAxisLabel;

		const original = response?.map(cost => processCost(cost, label1, toUnit));
		const combineTypeSeries = response?.map(cost => processCost(cost, label2, config.isCombineType && !config.isFragmentation ? 'none' : toUnit));


		if(config.isDrillName) {
			datasetName = response?.map(cost => cost.drillMachineName);
		} else {
			datasetName = response?.map(cost => cost.datasetName);
		}

		let seriesData :Series[] = [];
		const uniqueSites = {};

		switch (true) {
			case config.isTruckGraph:
				const truckGraph = this.generateTruckGraphSeriesData(response);
				let data = Object.values(truckGraph).flat().map(res => Number(res["explosiveAmount"]));
				datasetName = Object.keys(truckGraph);
				data = data.map(cost => {
					const converted = measurement.convert(cost, toUnit);
					return Math.round(converted * 100) / 100;
				});
				seriesData = [
					{ name: yAxisLabel, data },
				];
				break;

			case this.siteId && config.isExplosiveGraph:
				seriesData = [
					{ name: yAxisLabel, data: original },
				];
				break;

			case config.isSizeRange && config.isOversizePercent:
				response = response?.filter(data => Object?.keys(data?.fragmentationSizeRanges)?.length > 0 && data.finesPercent);
				datasetName = response?.map(cost => cost.datasetName);
				seriesData = [{name: yAxisLabel, data: response?.map((dataset)=>{return Number(measurement.toMaxDecimals(dataset.fragmentationSizeRanges.rocksAreaPercent[dataset.fragmentationSizeRanges.rocksAreaPercent?.length - 1], 2))})}];
				break;

			case config.isSizeRange:
				response = response?.filter(data => Object?.keys(data?.fragmentationSizeRanges)?.length > 0);
				datasetName = response?.map(cost => cost.datasetName);
				seriesData = this.generateSizeRangeSeriesData(response);
				break;

			case config.isCombineType && config.isLabourGraph:
				response.forEach(item => {
					if (!(item.siteId in uniqueSites)) {
						uniqueSites[item.siteId] = item.siteName;
					}
				});
				datasetName = Object.values(uniqueSites);
				seriesData = this.generateLabourAndHourSeriesData(response);
				break;

			case config.isCombineType && !config.isDrillAndBlastCosts:
				seriesData = [
					{ name: this.formatLabel(label1), data: original, type: config.seriesType },
					{ name: this.formatLabel(label2), data: combineTypeSeries, type: config.seriesType1 },
				];
				break;

			case config.isDrillAndBlastCosts:
				const originalData = response.map(cost => Number(cost[label1]));
				const goals = response.map(cost => Number(cost[label2]));
				seriesData = [
					{ name: yAxisLabel , data: originalData },
					{ name: `${yAxisLabel} Goal`, data: goals },
				];
				break;

			case config.isInventoryGraph:
				const reports = this.normalizeInventoryReports(response as unknown as InventoryApiResponse[]);

				if (config.isDonut) {
					config.chartTitle = this.siteId ? 'Total volume per material' : 'Total volume per site';

					const volumeMap = this.siteId ? this.aggregateVolume(reports, r => r.stockpile.materialType?.name, r => r.stockpile.volume) : this.aggregateVolume(reports, r => r.site.name, r => r.stockpile.volume);
					seriesData = this.buildDonutSeries(volumeMap);
					break;
				}
				if (this.siteService.stockpileId$.value && this.siteService.seletedStockpileName$.value) {
					config.chartTitle = 'Stockpile History';
					config.xAxisLabel = 'Date';
					yAxisLabel = 'Volume';

					const filtered = reports.filter(r => r.stockpile.name === this.siteService.seletedStockpileName$.value).sort((a, b) => new Date(a.dataset.createdAt).getTime() - new Date(b.dataset.createdAt).getTime());

					const volumeMap = this.aggregateVolume(filtered, r => new Date(r.dataset.createdAt).toDateString(), r => r.stockpile.volume);

					seriesData = this.buildDonutSeries(volumeMap);
					datasetName = Array.from(volumeMap.keys());
					break;
				}

				const { volume, weight } = this.aggregateMaterialData(reports);
				seriesData = this.buildColumnSeries(volume, weight);
				datasetName = Array.from(volume.keys());
				break;

			case config.isDrillName && config.isHoleFeature:
				const holeFeatureKeys = new Set<string>();
				response.forEach(record => {
					if (record.holeFeatures) {
						Object.keys(record.holeFeatures).forEach(key => holeFeatureKeys.add(key));
					}
				});
				seriesData = Array.from(holeFeatureKeys).map((feature, index) => {
					return {
						type: 'column',
						name: feature,
						data: response.map(record => record.holeFeatures[feature] || 0)
					};
				});
				break;

			default:
				seriesData = [
					{ name: yAxisLabel, data: original },
				];
				break;
		}

		const chartOptionsData =  this.chartOptions(config.chartType, datasetName, config.chartTitle, seriesData, yAxisLabel, config.xAxisLabel, unitAbbreviation, config.isOversizePercent, config.isFinesPercent);
		config.isCombineType ? chartOptionsData.chart['isTotalDrillHoleByLength'] = true : ''
		config.isDrillName && config.isHoleFeature ? chartOptionsData.chart['isHoleFeatureData'] = true : ''
		if(config.isSizeRange && !config.isOversizePercent) {
			chartOptionsData.chart['isFragmentSizeRangeChart'] = true;
			const unit = this.user.unit('smallDistance');
			const minDiameterStr = response.map(dataset => measurement.format(dataset.minimumRocksDiameter, { unit }));
			const finesName = `Fines < ${minDiameterStr}`;
			if(this.siteId) {
				chartOptionsData['oversizeSeriesData'] = {
					data: response.map(data => data?.oversizeGoal),
					datasetIds: response.map(data => data.datasetId)
				}

				chartOptionsData['goalSeriesData'] = {
					name: `${finesName}`,
					data: response.map(data => Number(measurement.toMaxDecimals(data.finesGoal, 2))),
					datasetIds: response.map(data => data.datasetId)
				}
			}
			else {
				chartOptionsData['goalSeriesData'] = {
					name: `${finesName}`,
					data: [],
					datasetIds: []
				}
				chartOptionsData['oversizeSeriesData'] = {
					data: [],
					datasetIds: []
				}
			}
		}

		chartOptionsData['unitAbbreviation'] = unitAbbreviation;

		if(config.isCombineType && config.isDesignBurdenGraph) {
			chartOptionsData.chart['isDesignBurdenGraph'] = true;
			chartOptionsData['datasetId'] = response.map(data => data.datasetId);
			chartOptionsData['designBurdens'] = response.map(data => data.designBurden);
		}

		if (config.isDrillAndBlastCosts) {
			chartOptionsData['currencySymbol'] = getCurrencySymbol(response[0]?.currencyCode, "narrow");
			chartOptionsData['isDrillAndBlastCosts'] = config.isDrillAndBlastCosts;
		}

		if (config.isSizeRange && config.isOversizePercent) {
			chartOptionsData.chart['isOversizePercent'] = true;
		}

		if (config.isFinesPercent) {
			chartOptionsData.chart['isFinesPercent'] = true;
		}

		if (config.isAverage) {
			chartOptionsData['isAverage'] = config.isAverage;
		}

		return chartOptionsData;
	}

	private formatLabel(label: string): string {
		return label.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
	}

	private generateSizeRangeSeriesData(response) {
		const resultsWithFragmentation = response.filter(res => res?.fragmentationSizeRanges?.rocksAreaPercent && res.finesPercent);
		let arrangeData = this.mergeFragmentationData(resultsWithFragmentation,this.user.unit('smallDistance'));
		return arrangeData;
	}


	private generateLabourAndHourSeriesData(response)  {
		const averagesBySite = response.reduce((acc, item) => {
			const siteId = item.siteId;
			acc[siteId] = acc[siteId] || { laborSum: 0, hoursSum: 0, count: 0 };
			acc[siteId].laborSum += item.noOfLabors;
			acc[siteId].hoursSum += item.hours;
			acc[siteId].count++;
			return acc;
		}, {});

		const dataArray: { laborSum: number,hoursSum:number, count: number }[] = Object.values(averagesBySite);

		const noOfLabors = dataArray.map(siteData => parseInt((siteData.laborSum / siteData.count).toFixed(2)));
		const noOfHours = dataArray.map(siteData => parseInt((siteData.hoursSum / siteData.count).toFixed(2)));

		const seriesData = [
			{ name: "Average laborers", data: noOfLabors, type: 'column' },
			{ name: "Average hours", data: noOfHours, type: 'column' },
		];
		return seriesData;
	}

	private generateTruckGraphSeriesData(response) {
		let truckData = response
			.map(res => res.trucks)
			.filter(Boolean)
			.flat()
			.reduce((acc, res) => {
				if (!acc[res.code]) {
					acc[res.code] = [];
				}
				acc[res.code].push(res);
				return acc;
		}, {});

		return  this.mergeExplosiveAmount(truckData)
	}

	private mergeExplosiveAmount(truckData) {
		const mergedData = {};
		Object.keys(truckData).forEach(code => {
			const mergedObject = truckData[code].reduce((acc, obj) => {
				if (acc.code === undefined) {
					acc = { ...obj };
				} else {
					acc.explosiveAmount = (parseInt(acc.explosiveAmount) + parseInt(obj.explosiveAmount)).toString();
				}
				return acc;
			}, {});
			mergedData[code] = [mergedObject];
		});

		return mergedData;
	}

	private chartOptions(chartType: string, xAxisCategories: string[], chartTitle: string, seriesData, yAxisLabel: string, xAxisLabel:string, unitAbbreviation: string, isOversizePercent?: boolean, isFinesPercent?: boolean) {

		const options:Highcharts.Options = {
			chart: {
				type: chartType,
				style: {
					fontFamily: 'Lato'
				}			},
			title: {
				text: `${chartTitle}`,
				align: 'left'
			},
			xAxis: {
				categories: xAxisCategories,
				title: {
					text: xAxisLabel,
					style: {
						fontSize: '1rem'
					}
				},
				labels: {
					style: {
						fontSize: '1rem'
					}
				}
			},
			yAxis: {
				title: {
					text: yAxisLabel === 'Volume, Weight' ? `${yAxisLabel}` : unitAbbreviation ? `${yAxisLabel} (${unitAbbreviation})` : isOversizePercent || isFinesPercent ? `${yAxisLabel} (%)` : `${yAxisLabel}`,
					style: {
						fontSize: '1rem'
					}
				},
				labels: {
					style: {
						fontSize: '1rem'
					}
				}
			},
			credits: {
				enabled: false
			},
			series: seriesData
		}
		return options;
	}

	private processTimelineDate(timeline: string) {
		const timelineRanges = { '1D': 1, '1W': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365 };
		const daysAgo = timelineRanges[timeline];
		const startDate = new Date();
		startDate.setDate(startDate.getDate() - daysAgo);
		return this.datePipe.transform(startDate, 'yyyy-MM-dd', 'UTC');
	}

	private mergeFragmentationData(filterResponse, unit) {
		let seriesData = [];
		const nameToIndexMap = new Map();

		const addDataToSeries = (name, datasetIndex, value) => {
			if (!nameToIndexMap.has(name)) {
				const entry = {
					type: 'column',
					name,
					data: Array(datasetIndex).fill(null)
				};
				entry.data.push(value);
				seriesData.push(entry);
				nameToIndexMap.set(name, seriesData.length - 1);
			} else {
				const index = nameToIndexMap.get(name);
				seriesData[index].data.push(value);
			}
		};

		filterResponse.forEach((dataset, datasetIndex) => {
			const minDiameterStr = measurement.format(dataset.minimumRocksDiameter, { unit });
			const finesName = `Fines < ${minDiameterStr}`;
			const finesPercent = Number(measurement.toMaxDecimals(dataset.finesPercent, 2));
			addDataToSeries(finesName, datasetIndex, finesPercent);

			if (!dataset.fragmentationSizeRanges.sizeMm.includes(Infinity) ||
				!dataset.fragmentationSizeRanges.sizeMm.includes(-Infinity)) {
				dataset.fragmentationSizeRanges.sizeMm = [-Infinity, ...dataset.fragmentationSizeRanges.sizeMm, Infinity];
			}

			dataset.fragmentationSizeRanges.sizeMm.forEach((size, index) => {
				if (index < dataset.fragmentationSizeRanges.sizeMm.length - 1) {
					const startStr = measurement.format(size / 1000, { unit });
					const endStr = measurement.format(dataset.fragmentationSizeRanges.sizeMm[index + 1] / 1000, { unit });
					const sizeName = `${startStr} to ${endStr}`;
					const percent = Number(measurement.toMaxDecimals(dataset.fragmentationSizeRanges.rocksAreaPercent[index] || 0, 2));
					addDataToSeries(sizeName, datasetIndex, percent);
				}
			});

			seriesData.forEach((entry) => {
				if (entry.data.length < datasetIndex + 1) {
					entry.data.push(null);
				}
			});
		});

		const sizeSortedSeriesData = seriesData
			.filter(entry => entry.name && !entry.name.startsWith("Fines"))
			.sort((a, b) => {
				const parseStart = (name) => parseFloat(name.split(' to ')[0].replace(unit, '').trim());
				const aStart = parseStart(a.name);
				const bStart = parseStart(b.name);
				if (isNaN(aStart)) return 1;
				if (isNaN(bStart)) return -1;
				return bStart - aStart;
			});

		const finesEntry = seriesData.find(entry => entry.name && entry.name.startsWith("Fines"));
		if (finesEntry) sizeSortedSeriesData.push(finesEntry);

		return sizeSortedSeriesData;
	}

	private normalizeInventoryReports(response: InventoryApiResponse[]) {
		return response.map(data => {
			const volume = Number(data.stockpile.volume) || 0;
			const density = Number(data.stockpile.materialType?.density) || 0;
			const unitPrice = Number(data.stockpile.materialPrice?.price) || 0;

			const weight = volume * density;
			const price = weight ? measurement.convert(weight, 'metricton') * unitPrice : 0;

			return {
				...data,
				stockpile: {
					...data.stockpile,
					volume,
					createdAt: new Date(data.stockpile.createdAt)
				},
				dataset: data.dataset?.createdAt
					? { ...data.dataset, createdAt: new Date(data.dataset.createdAt) }
					: data.dataset,
				weight,
				price
			};
		});
	}

	private aggregateVolume<T>(data: T[], keyFn: (item: T) => string, volumeFn: (item: T) => number): Map<string, number> {
		return data.reduce((map, item) => {
			const key = keyFn(item);
			if (!key) return map;

			map.set(key, (map.get(key) || 0) + math.max(0, volumeFn(item)));
			return map;
		}, new Map<string, number>());
	}

	private aggregateMaterialData(reports: Array<{ stockpile: Pick<StockpileData, 'volume' | 'materialType'> }>) {
		return reports.reduce((acc, data) => {
				const material = data.stockpile.materialType;
				if (!material) return acc;

				const volume = math.max(0, data.stockpile.volume);

				acc.volume.set(material.name, volume + (acc.volume.get(material.name) || 0));
				acc.weight.set(material.name, volume * material.density + (acc.weight.get(material.name) || 0));
				return acc;
			},
			{ volume: new Map<string, number>(), weight: new Map<string, number>() }
		);
	}

	private buildDonutSeries(map: Map<string, number>) {
		return Array.from(map, ([name, value]) => ({
			name,
			data: [value],
			type: 'pie'
		}));
	}

	private buildColumnSeries(volume: Map<string, number>, weight: Map<string, number>) {
		const weightUnit = this.userUnitMeasurement === 'imperial' ? updatedTypeMeta.largeWeight.imperialUnit : updatedTypeMeta.largeWeight.metricUnit;
		const volumeUnit = this.userUnitMeasurement === 'imperial' ? updatedTypeMeta.volume.imperialUnit : updatedTypeMeta.volume.metricUnit;
		return [
			{
				name: 'Volume',
				data: Array.from(volume.values()).map(v => Number(measurement.toMaxDecimals(measurement.convert(v, volumeUnit), 2))),
				type: 'column'
			},
			{
				name: 'Weight',
				data: Array.from(weight.values()).map(v => Number(measurement.toMaxDecimals(measurement.convert(v, weightUnit), 2))),
				type: 'column'
			}
		];
	}
}

interface InventoryApiResponse {
	stockpile: StockpileData;
	dataset?: Dataset;
	site: Site;
}
